/**
 * Reordering org-configured sidebar pages (task pages and agent pages).
 *
 * Those pages share one list inside a sidebar group, ordered by `sortOrder`
 * then name. Built-in routes and built-in placements are not pages here:
 * they stay where the merge put them, and a move only swaps a page with the
 * neighbouring page in that group.
 *
 * Sort orders are not unique, so a swap of two equal numbers would not change
 * what the sidebar shows. The new order is written back onto a strictly
 * increasing sequence drawn from the orders already in the group.
 */

/** Matches `SidebarSortOrderSchema` in the page contracts. */
export const SIDEBAR_SORT_ORDER_MAX = 10000;

export type SidebarPageKind = 'task' | 'agent';

export type OrderedSidebarPage = {
  kind: SidebarPageKind;
  id: string;
  sortOrder: number;
};

export type PageSortUpdate = {
  kind: SidebarPageKind;
  id: string;
  sortOrder: number;
};

/** Update/Delete grants for the two page kinds the sidebar menu can edit. */
export type SidebarPageGrants = {
  taskUpdate: boolean;
  taskDelete: boolean;
  agentUpdate: boolean;
  agentDelete: boolean;
};

/** What the signed-in user may do to one page kind. */
export function sidebarPageGrants(
  kind: SidebarPageKind,
  grants: SidebarPageGrants,
): { canUpdate: boolean; canDelete: boolean } {
  if (kind === 'task') {
    return { canUpdate: grants.taskUpdate, canDelete: grants.taskDelete };
  }
  return { canUpdate: grants.agentUpdate, canDelete: grants.agentDelete };
}

/**
 * The page a right-click may act on. Null for built-in routes, unknown pages,
 * and pages the user can neither update nor delete.
 */
export function manageableSidebarPage(
  entryId: string,
  grants: SidebarPageGrants,
  knownEntryIds: ReadonlySet<string>,
): { kind: SidebarPageKind; id: string } | null {
  const ref = sidebarPageRef(entryId);
  if (!ref || !knownEntryIds.has(entryId)) return null;
  const access = sidebarPageGrants(ref.kind, grants);
  if (!access.canUpdate && !access.canDelete) return null;
  return ref;
}

/** Whether a page can move up or down among `pages` (sidebar order). */
export function sidebarPageMoveAvailability(
  pages: readonly OrderedSidebarPage[],
  target: { kind: SidebarPageKind; id: string },
): { up: boolean; down: boolean } {
  return {
    up: pageMoveUpdates(pages, target, 'up') !== null,
    down: pageMoveUpdates(pages, target, 'down') !== null,
  };
}

const TASK_PAGE_ENTRY_PREFIX = 'task-page:';
const AGENT_PAGE_ENTRY_PREFIX = 'agent-page:';

/** The configurable page a sidebar entry points at, or null for anything else. */
export function sidebarPageRef(
  entryId: string,
): { kind: SidebarPageKind; id: string } | null {
  const prefix = entryId.startsWith(TASK_PAGE_ENTRY_PREFIX)
    ? TASK_PAGE_ENTRY_PREFIX
    : entryId.startsWith(AGENT_PAGE_ENTRY_PREFIX)
      ? AGENT_PAGE_ENTRY_PREFIX
      : null;
  if (!prefix) return null;
  const id = entryId.slice(prefix.length);
  if (id.length === 0) return null;
  return { kind: prefix === TASK_PAGE_ENTRY_PREFIX ? 'task' : 'agent', id };
}

export function sidebarPageEntryId(page: {
  kind: SidebarPageKind;
  id: string;
}): string {
  const prefix =
    page.kind === 'task' ? TASK_PAGE_ENTRY_PREFIX : AGENT_PAGE_ENTRY_PREFIX;
  return `${prefix}${page.id}`;
}

/** Route the page is mounted on. Mirrors the nav entry builders. */
export function sidebarPagePath(kind: SidebarPageKind, id: string): string {
  return kind === 'task' ? `/pages/${id}` : `/agent-pages/${id}`;
}

/** Whether the reader is on that page (exact path or a child route). */
export function isViewingSidebarPage(
  pathname: string,
  fullPath: string,
): boolean {
  return pathname === fullPath || pathname.startsWith(`${fullPath}/`);
}

/**
 * Configurable pages in the order their sidebar entries are rendered.
 * Entries that are not pages, or that have no known sort order, are skipped.
 */
export function configurablePagesInOrder(
  entryIds: readonly string[],
  sortOrderByEntryId: ReadonlyMap<string, number>,
): OrderedSidebarPage[] {
  const pages: OrderedSidebarPage[] = [];
  for (const entryId of entryIds) {
    const ref = sidebarPageRef(entryId);
    const sortOrder = sortOrderByEntryId.get(entryId);
    if (!ref || sortOrder === undefined) continue;
    pages.push({ kind: ref.kind, id: ref.id, sortOrder });
  }
  return pages;
}

/**
 * Sort-order writes that move `target` one place in `pages` (already in
 * sidebar order). Null when it is not in the list or already at that end.
 * The input list is not mutated. Only pages whose order actually changes
 * are returned.
 */
export function pageMoveUpdates(
  pages: readonly OrderedSidebarPage[],
  target: { kind: SidebarPageKind; id: string },
  direction: 'up' | 'down',
): PageSortUpdate[] | null {
  const index = pages.findIndex(
    (page) => page.kind === target.kind && page.id === target.id,
  );
  const neighborIndex = index + (direction === 'up' ? -1 : 1);
  if (index < 0 || neighborIndex < 0 || neighborIndex >= pages.length) {
    return null;
  }
  const reordered = moveItem(pages, index, neighborIndex);
  const assigned =
    raisedSortOrders(pages.map((page) => page.sortOrder)) ??
    compactSortOrders(pages.length);
  if (!reordered || !assigned) return null;
  return updatesFor(reordered, assigned);
}

function moveItem<T>(
  items: readonly T[],
  from: number,
  to: number,
): T[] | null {
  const moving = items[from];
  if (moving === undefined) return null;
  const next = items.slice();
  next.splice(from, 1);
  next.splice(to, 0, moving);
  return next;
}

/**
 * The same multiset of orders, made strictly increasing without going past
 * {@link SIDEBAR_SORT_ORDER_MAX}. Null when a later order would have to.
 */
function raisedSortOrders(orders: readonly number[]): number[] | null {
  const sorted = orders.slice().sort((a, b) => a - b);
  const raised: number[] = [];
  for (const order of sorted) {
    const previous = raised[raised.length - 1];
    const next = previous === undefined ? order : Math.max(order, previous + 1);
    if (next > SIDEBAR_SORT_ORDER_MAX) return null;
    raised.push(next);
  }
  return raised;
}

/** 0..n-1, or null when that sequence cannot fit in the sort-order range. */
function compactSortOrders(count: number): number[] | null {
  if (count - 1 > SIDEBAR_SORT_ORDER_MAX) return null;
  return Array.from({ length: count }, (_, index) => index);
}

function updatesFor(
  pages: readonly OrderedSidebarPage[],
  assigned: readonly number[],
): PageSortUpdate[] {
  const updates: PageSortUpdate[] = [];
  for (let index = 0; index < pages.length; index += 1) {
    const page = pages[index];
    const sortOrder = assigned[index];
    if (!page || sortOrder === undefined || sortOrder === page.sortOrder) {
      continue;
    }
    updates.push({ kind: page.kind, id: page.id, sortOrder });
  }
  return updates;
}
